import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Users } from 'lucide-react';
import { EditableCardActions } from './EditableCardActions';
import { useEditableCard } from '@/hooks/useEditableCard';
import { updateSchool } from '@/api/schools';
import type { SchoolEditData, SchoolOptions, UpdateSchoolData } from '@/types/school';

interface ContactsCardProps {
  school: SchoolEditData;
  options: SchoolOptions;
  onSaved: () => void;
}

interface ContactsFormData {
  [key: string]: unknown;
  retailerId: number | null;
  technicalContactId: number | null;
  salesContactId: number | null;
}

export function ContactsCard({ school, options, onSaved }: ContactsCardProps) {
  const initialData: ContactsFormData = {
    retailerId: school.retailerId,
    technicalContactId: school.technicalContactId,
    salesContactId: school.salesContactId,
  };

  const { isEditing, formData, isSaving, error, startEditing, cancelEditing, updateField, save } =
    useEditableCard<ContactsFormData>({
      initialData,
      onSave: async (data) => {
        const payload: UpdateSchoolData = {
          retailerId: data.retailerId,
          technicalContactId: data.technicalContactId,
          salesContactId: data.salesContactId,
        };
        await updateSchool(school.code, payload);
      },
      onSaved,
    });

  if (!isEditing) {
    return (
      <Card>
        <CardHeader
          icon={<Users />}
          iconColor="cyan"
          actions={
            <EditableCardActions
              isEditing={false}
              isSaving={false}
              onEdit={startEditing}
              onSave={save}
              onCancel={cancelEditing}
            />
          }
        >
          <CardTitle>Contacts</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {school.retailerId && (
            <div>
              <p className="text-sm font-medium text-muted-foreground">Apporteur d'affaires</p>
              <p className="text-sm">
                {options.users.find((u) => u.id === school.retailerId)?.fullname || '-'}
              </p>
            </div>
          )}
          {school.technicalContactId && (
            <div>
              <p className="text-sm font-medium text-muted-foreground">Contact technique</p>
              <p className="text-sm">
                {options.users.find((u) => u.id === school.technicalContactId)?.fullname || '-'}
              </p>
            </div>
          )}
          {school.salesContactId && (
            <div>
              <p className="text-sm font-medium text-muted-foreground">Contact commercial</p>
              <p className="text-sm">
                {options.users.find((u) => u.id === school.salesContactId)?.fullname || '-'}
              </p>
            </div>
          )}
          {!school.retailerId && !school.technicalContactId && !school.salesContactId && (
            <p className="text-sm text-muted-foreground">Aucun contact defini</p>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        icon={<Users />}
        iconColor="cyan"
        actions={
          <EditableCardActions
            isEditing={true}
            isSaving={isSaving}
            onEdit={startEditing}
            onSave={save}
            onCancel={cancelEditing}
          />
        }
      >
        <CardTitle>Contacts</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="ct-retailer">Apporteur d'affaires</Label>
          <Select
            value={formData.retailerId?.toString() || 'none'}
            onValueChange={(value) =>
              updateField('retailerId', value === 'none' ? null : parseInt(value))
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Selectionner" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Aucun</SelectItem>
              {options.users.map((u) => (
                <SelectItem key={u.id} value={u.id.toString()}>
                  {u.fullname}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="ct-technical">Contact technique</Label>
          <Select
            value={formData.technicalContactId?.toString() || 'none'}
            onValueChange={(value) =>
              updateField('technicalContactId', value === 'none' ? null : parseInt(value))
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Selectionner" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Aucun</SelectItem>
              {options.users.map((u) => (
                <SelectItem key={u.id} value={u.id.toString()}>
                  {u.fullname}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="ct-sales">Contact commercial</Label>
          <Select
            value={formData.salesContactId?.toString() || 'none'}
            onValueChange={(value) =>
              updateField('salesContactId', value === 'none' ? null : parseInt(value))
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Selectionner" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Aucun</SelectItem>
              {options.users.map((u) => (
                <SelectItem key={u.id} value={u.id.toString()}>
                  {u.fullname}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardContent>
    </Card>
  );
}
